import { nanoid } from "@reduxjs/toolkit";
import React, { FC, useEffect } from "react";
import { formatMoney, formatPhone } from "shared/plugins/string-helper";
import moment from "moment-timezone";
import "./order-success.less";
import { shallowEqual, useSelector } from "react-redux";
import { momentDateTimeFormat } from "shared/plugins/localization";
import { RootState } from "app.store";
import { IConfirmCheckoutResponseModel, PaymentMethod, ShippingTypeEnum } from "states/order/order.models";

const OrderSuccessPage: FC = (props) => {
  const currentBranch = useSelector(
    (state: RootState) => state.shoppingCart.shoppingCart.branch,
    shallowEqual
  );

  const model = useSelector(
    (state: RootState) => state.order.confirmCheckoutSuccessModel,
    shallowEqual
  );

  useEffect(() => {
    document.title = "Order Success";
  }, []);
  return (
    <section id="order-success-page" className="confirmation-container">
      <div className="success-header">
        <h1 className="success-title">Thanks For Your Order</h1>
        <div className="customer-name">  {`${model?.firstName} ${model?.lastName}`.toUpperCase()}</div>
      </div>
      <div className="content">
        <div className="location-badge">
          <span className="location-icon">📍</span>
          <span>{model?.branch?.name}</span>
        </div>
        <div className="order-number-box">
          <div className="order-number-label">Order Number</div>
          <div className="order-number-value">{model?.orderNumber}</div>
        </div>
        <div className="section">
          <div className="section-header">📦 Order Confirmation</div>
          <div className="section-content">
            {model?.orderItems?.map((m) => (
              <div className="order-item" key={nanoid()}>
                <div className="item-header">
                  <div>
                    <div className="item-name"> {m.product?.name} | {formatMoney(m.price, 2, ".", ",", "$")}</div>
                    <div className="item-details">
                      {m.modifiers?.map((mo) => (
                        <span key={nanoid()}>{mo.product?.name} | Qty: {mo.quantity} | {formatMoney(mo.price * mo.quantity, 2, ".", ",", "$")}</span>
                      ))}
                    </div>
                    <div className="item-quantity">Qty: {m.quantity}</div>
                  </div>
                  <div className="item-price">{formatMoney((m.price + m.modifiers.map((mo) => mo.price * mo.quantity).reduce((a, b) => a + b, 0)) * m.quantity, 2, ".", ",", "$")}</div>
                </div>
              </div>

            ))}

          </div>
        </div>
        <div className="section">
          <div className="section-header">💰 Summary</div>
          <div className="section-content">
            <div className="summary-row">
              <span className="summary-label">Subtotal</span>
              <span className="summary-value">{formatMoney(model?.subTotal, 2, ".", ",", "$")}</span>
            </div>
            <div className="summary-row">
              <span className="summary-label">Discount</span>
              <span className="summary-value">-{formatMoney(model?.discountAmount, 2, ".", ",", "$")}</span>
            </div>
            <div className="summary-row">
              <span className="summary-label">Shipping</span>
              <span className="summary-value">{formatMoney(model?.shippingFee, 2, ".", ",", "$")}</span>
            </div>
            <div className="summary-row">
              <span className="summary-label">Taxes</span>
              <span className="summary-value">{formatMoney(model?.taxes, 2, ".", ",", "$")}</span>
            </div>
            {model?.additionalTaxes?.map((m, index) => (
              <div className="summary-row" key={nanoid()}>
                <span className="summary-label">{m.taxName}</span>
                <span className="summary-value">{formatMoney(m.feeAmount, 2, ".", ",", "$")}</span>
              </div>
            ))}
            <div className="summary-row">
              <span className="summary-label">Convenient fee</span>
              <span className="summary-value">{formatMoney(model?.nonCashAdjFee, 2, ".", ",", "$")}</span>
            </div>
            <div className="summary-row">
              <span className="summary-label">Tips</span>
              <span className="summary-value">{formatMoney(model?.tip, 2, ".", ",", "$")}</span>
            </div>

            <div className="summary-total">
              <div className="summary-row">
                <span className="summary-label">Total</span>
                <span className="summary-value">{formatMoney(model?.totalAmount, 2, ".", ",", "$")}</span>
              </div>
            </div>
          </div>
        </div>
        {model?.orderShippings[0] && (
          <div className="section">
            <div className="section-header">📋 Billing Information</div>
            <div className="section-content">
              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">Request pickup time</span>
                  <span className="info-value info-highlight"> {moment(model.orderShippings[0].requestedPickupTime)
                    .tz(currentBranch.ianaTimeZone)
                    .format(momentDateTimeFormat())}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Payment method</span>
                  <span className="info-value">Credit Card</span>
                </div>
                {model?.orderPayments.length > 0 && (
                  <>
                    <div className="info-item">
                      <span className="info-label">Name</span>
                      <span className="info-value">{`${model?.orderPayments[0].billingAddress.firstName} ${model?.orderPayments[0].billingAddress.lastName}`.toUpperCase()}</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Phone</span>
                      <span className="info-value"> {formatPhone(model?.phoneNumber)}</span>
                    </div>
                    {model?.orderPayments[0].billingAddress.countryCode !== "" && model?.orderPayments[0].billingAddress.countryCode !== null && (
                      <div className="info-item">
                        <span className="info-label">Address:</span>
                        <span className="info-value">
                          {`${model?.orderPayments[0].billingAddress.street},`}
                          <br />
                          {`${model?.orderPayments[0].billingAddress.provinceName}, `}
                          {`${model?.orderPayments[0].billingAddress.stateCode}, `}
                          {`${model?.orderPayments[0].billingAddress.postalCode}`}
                        </span>
                      </div>
                    )}
                  </>
                )}

              </div>
            </div>
          </div>
        )}
        <div className="email-notice">
          <span className="email-icon">📧</span>
          {model?.email && (
            <div className="email-text">
              Please check your mail box: <span className="email-address">{model?.email}</span>
            </div>
          )}
        </div>
        <a className="action-button" href="/">
          GO HOMEPAGE
        </a>
      </div>

    </section>
  );
};

export default OrderSuccessPage;
